import { useEffect, useRef, useState } from 'react';

// Polls /api/version and flips `updateAvailable` when the deployed build
// differs from the bundle the user is running. Check runs on mount, on
// an interval, and whenever the tab becomes visible again.
//
// The reference version is the BUILD-TIME SHA baked into the bundle via
// REACT_APP_BUILD_VERSION (set by heroku-postbuild). That makes the
// stale-bundle case work: if you boot with an old SW-cached bundle, the
// first /api/version response already differs from the baked-in value,
// and the banner appears immediately. Falls back to the first server
// response when no build-time value is available (local dev).
const BUILD_VERSION = (process.env.REACT_APP_BUILD_VERSION || '').trim() || null;

export const useVersionCheck = ({ pollIntervalMs = 120000 } = {}) => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const referenceVersionRef = useRef(BUILD_VERSION);

  useEffect(() => {
    let cancelled = false;

    const fetchVersion = async () => {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.version || null;
      } catch {
        return null;
      }
    };

    const check = async () => {
      const current = await fetchVersion();
      if (cancelled || !current) return;
      if (referenceVersionRef.current === null) {
        // No build-time SHA (local dev / build env without
        // HEROKU_SLUG_COMMIT). Anchor on the first server response so
        // we still catch deploys-while-on-page.
        referenceVersionRef.current = current;
      } else if (current !== referenceVersionRef.current) {
        setUpdateAvailable(true);
      }
    };

    check();
    const timer = setInterval(check, pollIntervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollIntervalMs]);

  return { updateAvailable };
};
